import React, { useState, useEffect } from 'react'
import { useTranslate, TextField, List, Datagrid, FunctionField, useNotify } from 'react-admin'
import { ShowElement } from 'src/components/uiElements/CRUD/ShowElement'
import EventIcon from '@mui/icons-material/Event'
import PersonIcon from '@mui/icons-material/Person'
import IconButton from '@mui/material/IconButton'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { iconAccordingProduct } from 'src/components/pages/sales/reservation/helper/IconAccordingProduct'
import { Backdrop, CircularProgress, Grid, TableCell, TableHead, TableRow } from '@mui/material'

import {
  ShowCard,
  RigthSideCard,
  LeftSideCard,
  CompleteSideCard,
} from 'src/components/uiElements/cards/ShowCard'
import { FormattedTextFieldWithStatus } from 'src/components/pages/sales/reservation/helper/FormattedTextFieldWithStatus'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
import { useRefresh } from 'react-admin'
import { cancelItemReservation } from 'src/components/pages/sales/reservation/helper/cancelItemReservation'
import { useQuery } from 'react-query'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'

export const GeneralSectionCreation = ({ reservation, editMode = false }) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()
  const [open, setOpen] = useState(false)
  const [nameReserve, setNameReserve] = useState('')
  const [hashItemReserve, setHashItemReserve] = useState('')
  const [loadingCancelItem, setLoadingCancelItem] = useState(false)
  const [confirm, setConfirm] = useState(false)

  //service to modals
  const allSellers = useQuery('user-all', () => reservationServices.getAllUser())

  useEffect(() => {
    if (confirm) {
      cancelItemReservation({
        hashItemReserve,
        nameReserve,
        refresh,
        notify,
        setLoadingCancelItem,
        setNameReserve,
        setHashItemReserve,
        setConfirm,
        setOpen,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm])

  const TabbedValueByProduct = (product, record) => {
    if (record?.id && typeof product === 'string' && !record?.product?.is_offline) {
      return (
        <IconButton
          size='small'
          onClick={() => {
            setOpen(true)
            setNameReserve(record?.external_id)
            setHashItemReserve(record?.hash_)
          }}
          disabled={record?.status === 'CANCELLED'}
          style={{ color: record?.status === 'CANCELLED' ? 'gray' : 'red' }}
        >
          <HighlightOffIcon />
        </IconButton>
      )
    }
  }

  const CustomHeader = ({ children }) => (
    <TableHead>
      <TableRow>
        {React.Children.map(children, (child) =>
          React.isValidElement(child) ? (
            <TableCell key={child.props.source} style={{ textAlign: child.props.textAlign || 'left' }}>
              <span>{child.props.label}</span>
            </TableCell>
          ) : null
        )}
      </TableRow>
    </TableHead>
  )

  const getManagerResponsible = (record) => {
    if (record && record?.business_unit_relation?.user_id && allSellers.data?.length > 0) {
      const ManagerResponsible = allSellers.data?.find(
        (user) => user?.id === record?.business_unit_relation?.user_id
      )
      if (ManagerResponsible) {
        return (
          <ShowElement
            data={`${ManagerResponsible?.firstname}, ${ManagerResponsible?.surname}`}
            label={translate('ra.field.managerResponsible')}
          />
        )
      }
    }

    return (
      <ShowElement
        data={allSellers.isLoading ? translate('ra.action.loading') : null}
        label={translate('ra.field.managerResponsible')}
      />
    )
  }

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loadingCancelItem}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12} xl={6}>
          <ShowCard
            iconHeader={<EventIcon />}
            title={translate('ra.title.general_information')}
            optionHeigth={'general'}
          >
            <LeftSideCard>
              <ShowElement data={reservation.number} label={translate('ra.field.number')} />
              <ShowElement data={reservation.status} label={translate('ra.field.status')} />
              <ShowElement data={reservation.date_in} label={translate('ra.field.date')} date />
            </LeftSideCard>
            <RigthSideCard>
              <ShowElement data={reservation.passenger} label={translate('ra.field.passenger')} blueText />
              <ShowElement
                data={numberFormatterWithDecimalAndThousand(reservation.amount_total)}
                label={translate('ra.field.amount')}
                blueText
                weightMoney
              />
              <ShowElement data={reservation.user} label={translate('ra.field.seller')} blueText />
              <ShowElement data={reservation.creator} label={translate('ra.field.user')} />
              <ShowElement data={reservation.create_at} label={translate('ra.field.created_at')} dateTime />
            </RigthSideCard>
          </ShowCard>
        </Grid>
        <Grid item xs={12} md={12} lg={12} xl={6}>
          <ShowCard iconHeader={<PersonIcon />} title={translate('ra.title.client')} optionHeigth={'general'}>
            <LeftSideCard>
              <ShowElement data={reservation?.business_unit_relation.id} label={translate('ra.field.id')} />
              <ShowElement
                data={reservation?.business_unit_relation.fantasy_name}
                label={translate('ra.field.name')}
              />
              <ShowElement
                data={reservation?.business_unit_relation.email}
                label={translate('ra.field.email')}
              />
            </LeftSideCard>
            <RigthSideCard>
              <ShowElement
                data={reservation?.business_unit_relation.business_type.long_name}
                label={translate('ra.field.business_type')}
                blueText
              />
              <ShowElement
                data={reservation?.business_unit_relation.fiscal_data_relation.name}
                label={translate('ra.field.business_name')}
              />
              {getManagerResponsible(reservation)}
            </RigthSideCard>
          </ShowCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={8} md={12} lg={12} xl={6}>
          <ShowCard title={translate('ra.title.products')}>
            <CompleteSideCard>
              <Datagrid data={reservation?.items || []} bulkActionButtons={false} header={<CustomHeader />}>
                <FunctionField
                  label={translate('ra.field.product')}
                  render={(record) => iconAccordingProduct(record.product.ref_table_detail)}
                />
                <TextField label={translate('ra.field.reserve')} source='external_id' sortable={false} />
                <FormattedTextFieldWithStatus
                  label={translate('ra.field.status')}
                  source='status'
                  sortable={false}
                />
                <FunctionField
                  label={translate('ra.field.amount')}
                  sortable={false}
                  render={(record) =>
                    record?.pricing_relation?.[0]?.final_price &&
                    `$ ${record?.pricing_relation?.[0].final_price}`
                  }
                />
                <FunctionField
                  label={translate('ra.action.actions')}
                  render={(record) => TabbedValueByProduct(record.product.ref_table_detail, record)}
                />
              </Datagrid>
            </CompleteSideCard>
          </ShowCard>
          <ConfirmationDialog
            title={translate('ra.message.cancel_item_reserva', { reserva: nameReserve })}
            buttonConfirmText={translate('ra.action.to_confirm')}
            buttonCancel={translate('ra.action.cancel')}
            loadingModalButton={loadingCancelItem}
            setOpen={setOpen}
            setConfirm={setConfirm}
            open={open}
          />
        </Grid>
        {/* <Grid item xs={8} md={12} lg={12} xl={6}>
          <ShowCard title={'Comentarios'}></ShowCard>
        </Grid> */}
      </Grid>
    </>
  )
}
